import IconButton from '@mui/material/IconButton'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableFooter from '@mui/material/TableFooter'
import TableHead from '@mui/material/TableHead'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import tableCellClasses from '@mui/material/TableCell/tableCellClasses'
import AddIcon from '@mui/icons-material/Add'
import DoneIcon from '@mui/icons-material/Done'
import CloseIcon from '@mui/icons-material/Close'
import ThumbUpIcon from '@mui/icons-material/ThumbUp'
import EditIcon from '@mui/icons-material/Edit'
import Grid from '@mui/material/Grid2'
import ErrorOverlay from 'Components/General/ErrorOverlay'
import AddToArrayButton from 'Components/reusable/Buttons/AddToArrayButton'
import CaleoIconButton from 'Components/reusable/IconButtons/CaleoIconButton'
import DeleteButton from 'Components/reusable/IconButtons/DeleteButton'
import InterestLevelPicker from 'Components/reusable/InputFields/InterestLevelPicker'
import SkillPicker from 'Components/reusable/InputFields/SkillPicker'
import CertificatePicker from 'Components/reusable/InputFields/CertificatePicker'
import { InputField } from 'Components/reusable/InputFields/InputField'
import { useNotificationPopup } from 'Components/reusable/Notification'
import TablePaginationActions from 'Components/reusable/Tables/CustomTable/TablePaginationActions'
import { personAPI } from 'api/PersonAPI'
import { useUser } from 'hooks'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ICertificate } from 'types/cvsInterfaces'
import { IError } from 'types/error'
import { ILearningGoal, LearningGoalStatus, LearningGoalType } from 'types/learningGoalsInterfaces'
import { ISkill, ISkillInterestLevel } from 'types/levelItemInterfaces'
import { chooseDBTranslation } from 'utils/translations'

interface ILearningGoalsCardProps {
  personId: number
  editable: boolean
}

type DraftGoal = Omit<ILearningGoal, 'id'>
type DraftGoalWithRelations = DraftGoal & {
  Skill?: ISkill
  Certificate?: ICertificate
}

const getEmptyGoal = (personId: number): DraftGoalWithRelations => ({
  personId,
  type: 'skill',
  skillId: null,
  certificateId: null,
  customName: null,
  interestLevel: null,
  status: 'planned',
})

const LearningGoalsCard: React.FC<ILearningGoalsCardProps> = ({ personId, editable }) => {
  const { t, i18n } = useTranslation()
  const { features } = useUser()
  const { setSuccessNotificationPopup, setNotificationPopup } = useNotificationPopup()

  const [goals, setGoals] = useState<ILearningGoal[]>([])
  const [backendError, setBackendError] = useState<IError>()
  const [adding, setAdding] = useState<boolean>(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [draft, setDraft] = useState<DraftGoalWithRelations>(getEmptyGoal(personId))
  const [page, setPage] = useState<number>(0)
  const [rowsPerPage, setRowsPerPage] = useState<number>(10)

  useEffect(() => {
    const controller = new AbortController()

    ;(async () => {
      if (!features.includes('learningGoals')) {
        return
      }

      try {
        const results = await personAPI.getLearningGoals(personId, controller)
        setGoals(results)
      } catch (error) {
        setBackendError(error as IError)
      }
    })()

    return () => controller.abort()
  }, [features, personId])

  const goalTypes: LearningGoalType[] = ['skill', 'industry', 'role', 'certificate', 'course']
  const statuses: LearningGoalStatus[] = ['requested', 'planned', 'in_progress', 'completed']

  const isValidDraft = useMemo(() => {
    if (!draft.interestLevel || draft.interestLevel <= 0) return false
    if (draft.type === 'skill') return !!draft.skillId
    if (draft.type === 'certificate') return !!draft.certificateId
    return !!draft.customName?.trim()
  }, [draft])

  const disabledSkillIds = useMemo(() => {
    return goals
      .filter(goal => goal.type === 'skill' && goal.skillId !== null)
      .filter(goal => (editingId !== null ? goal.id !== editingId : true))
      .map(goal => goal.skillId as number)
  }, [goals, editingId])

  const disabledCertificateIds = useMemo(() => {
    return goals
      .filter(goal => goal.type === 'certificate' && goal.certificateId !== null)
      .filter(goal => (editingId !== null ? goal.id !== editingId : true))
      .map(goal => goal.certificateId as number)
  }, [goals, editingId])

  const isDuplicateDraft = useMemo(() => {
    const normalize = (value: string | null | undefined) => value?.trim().toLowerCase() ?? ''

    return goals.some(goal => {
      if (editingId !== null && goal.id === editingId) {
        return false
      }

      if (goal.type !== draft.type) {
        return false
      }

      if (draft.type === 'skill') {
        return draft.skillId !== null && goal.skillId === draft.skillId
      }

      if (draft.type === 'certificate') {
        return draft.certificateId !== null && goal.certificateId === draft.certificateId
      }

      return normalize(goal.customName) !== '' && normalize(goal.customName) === normalize(draft.customName)
    })
  }, [draft.certificateId, draft.customName, draft.skillId, draft.type, editingId, goals])

  const resetDraft = () => {
    setDraft(getEmptyGoal(personId))
    setAdding(false)
    setEditingId(null)
  }

  const getGoalName = (goal: ILearningGoal) => {
    if (goal.type === 'skill' && goal.Skill) {
      return chooseDBTranslation(i18n, goal.Skill).name
    }

    if (goal.type === 'certificate' && goal.Certificate) {
      return chooseDBTranslation(i18n, goal.Certificate).certificate
    }

    return goal.customName ?? '-'
  }

  const startEdit = (goal: ILearningGoal) => {
    setEditingId(goal.id)
    setAdding(false)
    setDraft({
      personId: goal.personId,
      type: goal.type,
      skillId: goal.skillId,
      certificateId: goal.certificateId,
      customName: goal.customName,
      interestLevel: goal.interestLevel,
      status: goal.status,
      Skill: goal.Skill,
      Certificate: goal.Certificate,
    })
  }

  const saveNew = async () => {
    if (isDuplicateDraft) {
      setNotificationPopup({
        message: t('profile.learningGoals.duplicateError'),
        type: 'warning',
        duration: 'short',
      })
      return
    }

    try {
      const created = await personAPI.createLearningGoal(personId, draft)
      setGoals(prev => [...prev, created])
      setSuccessNotificationPopup()
      resetDraft()
    } catch (error) {
      setBackendError(error as IError)
    }
  }

  const saveEdit = async () => {
    if (!editingId) return

    if (isDuplicateDraft) {
      setNotificationPopup({
        message: t('profile.learningGoals.duplicateError'),
        type: 'warning',
        duration: 'short',
      })
      return
    }

    try {
      const updated = await personAPI.updateLearningGoal(editingId, draft)
      setGoals(prev => prev.map(goal => (goal.id === editingId ? updated : goal)))
      setSuccessNotificationPopup()
      resetDraft()
    } catch (error) {
      setBackendError(error as IError)
    }
  }

  const removeGoal = async (id: number) => {
    try {
      await personAPI.deleteLearningGoal(id)
      setGoals(prev => prev.filter(goal => goal.id !== id))
      setSuccessNotificationPopup()
      resetDraft()
    } catch (error) {
      setBackendError(error as IError)
    }
  }

  const renderInterestLevel = (level: number | null) => {
    if (!level) {
      return (
        <Typography variant="body2" color="error">
          {t('profile.learningGoals.columns.unspecified')}
        </Typography>
      )
    }
    return (
      <>
        {Array.from(Array(level), (_x, i) => (
          <ThumbUpIcon key={i} fontSize="small" style={{ padding: 1 }} color="secondary" />
        ))}
      </>
    )
  }

  const renderNameEditor = () => {
    if (draft.type === 'skill') {
      return (
        <SkillPicker
          type="skill"
          value={(draft.Skill as ISkill) ?? null}
          usedSkillIds={disabledSkillIds}
          onChange={chosen =>
            setDraft(prev => ({
              ...prev,
              skillId: chosen?.id ?? null,
              customName: null,
              certificateId: null,
              Skill: chosen as ISkill,
              Certificate: undefined,
            }))
          }
          placeholderLabel={t('profile.learningGoals.searchSkill')}
          disableClearable
        />
      )
    }

    if (draft.type === 'certificate') {
      return (
        <CertificatePicker
          value={(draft.Certificate as ICertificate) ?? null}
          usedCertificateIds={disabledCertificateIds}
          onChange={chosen =>
            setDraft(prev => ({
              ...prev,
              certificateId: chosen?.id ?? null,
              customName: null,
              skillId: null,
              Certificate: chosen ?? undefined,
              Skill: undefined,
            }))
          }
          placeholderLabel={t('profile.learningGoals.searchCertificate')}
          disableClearable
          resetOnSelect={false}
        />
      )
    }

    return (
      <InputField
        value={draft.customName ?? ''}
        onChange={event =>
          setDraft(prev => ({ ...prev, customName: event.target.value, skillId: null, certificateId: null }))
        }
        placeholder={t('profile.learningGoals.customNamePlaceholder')}
      />
    )
  }

  if (!features.includes('learningGoals')) {
    return null
  }

  if (backendError && backendError.name !== 'CanceledError' && backendError.name !== 'AbortError') {
    return <ErrorOverlay error={backendError} setOpen={setBackendError} />
  }

  return (
    <Grid container mt={4}>
      <Grid size={12}>
        <Typography variant="h3" gutterBottom fontWeight="bold">
          {t('profile.learningGoals.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('profile.learningGoals.description')}
        </Typography>
      </Grid>

      <Grid size={12}>
        {editable && !adding && editingId === null && (
          <AddToArrayButton
            icon={<AddIcon color="primary" fontSize="large" />}
            tooltip={t('add')}
            maxWidth={true}
            clickAction={() => {
              setEditingId(null)
              setAdding(true)
              setDraft(getEmptyGoal(personId))
            }}
          />
        )}
        <Table
          size="small"
          sx={{
            [`& .${tableCellClasses.root}`]: {
              borderBottom: 'none',
            },
          }}
        >
          <TableHead
            sx={{ borderBottom: '1px solid #e0e0e0', backgroundColor: '#FFFFFF', position: 'sticky', top: 80 }}
          >
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('profile.learningGoals.columns.type')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('profile.learningGoals.columns.item')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('profile.learningGoals.columns.interest')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('profile.learningGoals.columns.status')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {adding && (
              <TableRow sx={{ '&:nth-of-type(odd)': { backgroundColor: 'action.hover' } }}>
                <TableCell>
                  <Select
                    size="small"
                    fullWidth
                    value={draft.type}
                    onChange={event => {
                      const nextType = event.target.value as LearningGoalType
                      setDraft(prev => ({
                        ...prev,
                        type: nextType,
                        skillId: null,
                        certificateId: null,
                        customName: null,
                        Skill: undefined,
                        Certificate: undefined,
                      }))
                    }}
                  >
                    {goalTypes.map(type => (
                      <MenuItem key={type} value={type}>
                        {t(`profile.learningGoals.types.${type}`)}
                      </MenuItem>
                    ))}
                  </Select>
                </TableCell>
                <TableCell>{renderNameEditor()}</TableCell>
                <TableCell>
                  <InterestLevelPicker
                    value={draft.interestLevel as ISkillInterestLevel | null}
                    onChange={value => setDraft(prev => ({ ...prev, interestLevel: value }))}
                    smallIcons
                  />
                </TableCell>
                <TableCell>
                  <Select
                    size="small"
                    fullWidth
                    value={draft.status}
                    onChange={event =>
                      setDraft(prev => ({ ...prev, status: event.target.value as LearningGoalStatus }))
                    }
                  >
                    {statuses.map(status => (
                      <MenuItem key={status} value={status}>
                        {t(`profile.learningGoals.status.${status}`)}
                      </MenuItem>
                    ))}
                  </Select>
                </TableCell>
                <TableCell align="right">
                  <CaleoIconButton
                    size="medium"
                    icon={<DoneIcon />}
                    tooltip={t('save')}
                    valid={isValidDraft && !isDuplicateDraft}
                    clickAction={() => {
                      if (editingId === null) {
                        void saveNew()
                      } else {
                        void saveEdit()
                      }
                    }}
                  />
                  <CaleoIconButton size="medium" icon={<CloseIcon />} tooltip={t('close')} clickAction={resetDraft} />
                </TableCell>
              </TableRow>
            )}

            {goals.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(goal =>
              editingId === goal.id ? (
                <TableRow key={goal.id} sx={{ '&:nth-of-type(odd)': { backgroundColor: 'action.hover' } }}>
                  <TableCell>
                    <Select
                      size="small"
                      fullWidth
                      value={draft.type}
                      onChange={event => {
                        const nextType = event.target.value as LearningGoalType
                        setDraft(prev => ({
                          ...prev,
                          type: nextType,
                          skillId: null,
                          certificateId: null,
                          customName: null,
                          Skill: undefined,
                          Certificate: undefined,
                        }))
                      }}
                    >
                      {goalTypes.map(type => (
                        <MenuItem key={type} value={type}>
                          {t(`profile.learningGoals.types.${type}`)}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell>{renderNameEditor()}</TableCell>
                  <TableCell>
                    <InterestLevelPicker
                      value={draft.interestLevel as ISkillInterestLevel | null}
                      onChange={value => setDraft(prev => ({ ...prev, interestLevel: value }))}
                      smallIcons
                    />
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      fullWidth
                      value={draft.status}
                      onChange={event =>
                        setDraft(prev => ({ ...prev, status: event.target.value as LearningGoalStatus }))
                      }
                    >
                      {statuses.map(status => (
                        <MenuItem key={status} value={status}>
                          {t(`profile.learningGoals.status.${status}`)}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell align="right">
                    <CaleoIconButton
                      size="medium"
                      icon={<DoneIcon />}
                      tooltip={t('save')}
                      valid={isValidDraft && !isDuplicateDraft}
                      clickAction={() => {
                        void saveEdit()
                      }}
                    />
                    <DeleteButton
                      size="medium"
                      clickAction={() => {
                        void removeGoal(goal.id)
                      }}
                    />
                    <CaleoIconButton size="medium" icon={<CloseIcon />} tooltip={t('close')} clickAction={resetDraft} />
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow
                  key={goal.id}
                  sx={{ '&:nth-of-type(odd)': { backgroundColor: 'action.hover' } }}
                  onClick={editable ? () => startEdit(goal) : undefined}
                  style={editable ? { cursor: 'pointer' } : undefined}
                >
                  <TableCell>{t(`profile.learningGoals.types.${goal.type}`)}</TableCell>
                  <TableCell>{getGoalName(goal)}</TableCell>
                  <TableCell>{renderInterestLevel(goal.interestLevel)}</TableCell>
                  <TableCell>{t(`profile.learningGoals.status.${goal.status}`)}</TableCell>
                  <TableCell align="right">
                    {editable && (
                      <IconButton
                        aria-label={t('edit')}
                        size="small"
                        onClick={event => {
                          event.stopPropagation()
                          startEdit(goal)
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              )
            )}
          </TableBody>
          <TableFooter sx={{ position: 'sticky', bottom: 0, insetBlockEnd: 0 }}>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                count={goals.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_event, newPage) => setPage(newPage)}
                onRowsPerPageChange={e => {
                  setRowsPerPage(Number(e.target.value) || 10)
                  setPage(0)
                }}
                labelRowsPerPage={t('custom-table.labelRowsSelect')}
                labelDisplayedRows={({ from, to, count }) => t('custom-table.labelDisplayedRows', { from, to, count })}
                ActionsComponent={TablePaginationActions}
                sx={{
                  [`& .MuiTablePagination-spacer`]: { display: 'none', order: 9 },
                  [`& .MuiTablePagination-displayedRows`]: { order: 4 },
                  [`& .MuiTablePagination-selectLabel`]: { ml: 1, order: 7 },
                }}
                slotProps={{
                  select: {
                    inputProps: { 'aria-label': t('custom-table.labelRowsSelect') },
                    native: true,
                    sx: { order: 8 },
                  },
                }}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </Grid>
    </Grid>
  )
}

export default LearningGoalsCard
